import { 
  BarChart as BChart, Bar, 
  Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, 
} from 'recharts';
import {formatAxisValue,getBarColor,getMultiBarColor} from './utils';
import { useTheme } from 'styled-components';
import {formatCellValue} from './utils';

const getXAxisTickMaxLength = (
  dataLength: number,
  configuredLength?: number,
  chartWidth = 520,
  marginLeft = -20,
  marginRight = 5,
) => {
  if (typeof configuredLength === 'number' && configuredLength > 0) {
    return configuredLength;
  }

  const safeLength = Math.max(1, dataLength);
  const plotWidth = Math.max(180, chartWidth - marginLeft - marginRight);
  const slotWidth = plotWidth / safeLength;
  const estimatedBarGap = slotWidth * 0.2;
  const availableLabelWidth = Math.max(28, slotWidth - estimatedBarGap - 8);
  const estimatedMaxChars = Math.floor(availableLabelWidth / 7);

  return Math.max(4, Math.min(18, estimatedMaxChars));
};

const CustomXAxisTick = ({ x, y, payload, maxLength }: any) => {
  const value = payload.value || '';
  const displayText = value.length > maxLength 
    ? value.substring(0, maxLength) + '...' 
    : value;

  return (
    <g transform={`translate(${x},${y})`}>
      <title>{value}</title>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill="#6b7280"
        fontSize={12}
      >
        {displayText}
      </text>
    </g>
  );
};



export const BarChart = ({commonProps,widget ,data}:any) =>{

    const theme = useTheme();
    const statusColors: Record<string, string> = widget.colors;
    const marginLeft = -20;
    const marginRight = 5;
    const parsedWidgetWidth = typeof widget?.width === 'number'
      ? widget.width
      : parseInt(widget?.width, 10);
    const estimatedChartWidth = Number.isFinite(parsedWidgetWidth) ? parsedWidgetWidth : 520;
    const dynamicTickMaxLength = getXAxisTickMaxLength(
      data?.length || 0,
      widget?.xAxisTickMaxLength,
      estimatedChartWidth,
      marginLeft,
      marginRight,
    );

    return (
        <BChart {...commonProps} 
             margin={{ top: 10, right: marginRight, left: marginLeft, bottom: 10 }}
            barCategoryGap="20%"  >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey={widget.xKey} stroke="#6b7280"  interval={0}  tick={(tickProps: any) => <CustomXAxisTick {...tickProps} maxLength={dynamicTickMaxLength} />}/>
            <YAxis stroke="#6b7280" tickFormatter={formatAxisValue} tick={{ fontSize: 12 }} domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}/>

            <Tooltip 
                formatter={(value:any,name:any) => [
                 formatCellValue(value, widget.isPricing === true),  
                null 
                ]}
                itemStyle={{ 
                  color: theme.darkMode ? '#58a6ff' : '#007bff',
                  fontSize: '12px',
                  paddingTop: '4px'
                }}
                labelFormatter={(label) => Array.isArray(widget.dataKey) ? `${widget.xKey}: ${label}` : label } 
                contentStyle={{ 
                  background: theme.darkMode ? '#1a1a1a' : 'white', 
                  color: theme.darkMode ? 'white' : 'black',
                  border: `1px solid ${theme.darkMode ? '#333' : '#e5e7eb'}`,
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
            />
            
            {Array.isArray(widget.dataKey) ? <Legend /> : null}

            {Array.isArray(widget.dataKey) ? (
                widget.dataKey.map((key:any, idx:any) => (
                <Bar 
                    key={key}
                    dataKey={key} 
                    radius={[4, 4, 0, 0]}
                    fill={statusColors[key]}
                >
                    {data?.map((entry:any, index:any) => (
                        <Cell key={`cell-${index}`} fill={getMultiBarColor(statusColors,key,entry,widget.fields,index)} />
                    ))}
                </Bar>
                ))
            ) : (
                <Bar dataKey={widget.valueKey} radius={[4, 4, 0, 0]}>
                    {data?.map((entry:any, index:any) => (
                    <Cell key={`cell-${index}`} fill={getBarColor(widget.valueKey,entry,widget.fields,index)} />
                    ))}
                </Bar>
            )}
         </BChart>
    )

}